export type CreatedEntityDestination = {
  id: string;
  destination: string;
};

type CreateWithFilesOptions<TResult> = {
  create: () => Promise<TResult>;
  getCreatedEntity: (result: TResult) => CreatedEntityDestination;
  saveFiles: (
    createdEntity: CreatedEntityDestination,
    result: TResult
  ) => Promise<void>;
};

type CreateWithFilesResult<TResult> = {
  result: TResult;
  createdEntity: CreatedEntityDestination;
};

type CreatedEntityFileSaveErrorOptions = CreatedEntityDestination & {
  cause: unknown;
  failureStage?: CreatedEntityFailureStage;
};

export type CreatedEntityFailureStage = 'files' | 'linking' | 'validation';

type SaveFilesForCreatedEntityOptions = {
  createdEntity: CreatedEntityDestination;
  saveFiles: () => Promise<void>;
};

type SaveFilesForOptionalCreatedEntityOptions = {
  createdEntity: CreatedEntityDestination | undefined;
  saveFiles: () => Promise<void>;
};

/**
 * Signals that the primary create succeeded but persistence of its files did
 * not. The destination is intentionally supplied by the calling surface: some
 * created child records have no standalone route and must return to their
 * persistent owning record instead.
 */
export class CreatedEntityFileSaveError extends Error {
  readonly createdEntityId: string;
  readonly destination: string;
  readonly failureStage: CreatedEntityFailureStage;

  constructor({
    id,
    destination,
    cause,
    failureStage = 'files',
  }: CreatedEntityFileSaveErrorOptions) {
    super(
      failureStage === 'files'
        ? 'Files failed to save after the entity was created'
        : `Post-create ${failureStage} failed after the entity was created`,
      { cause }
    );
    this.name = 'CreatedEntityFileSaveError';
    this.createdEntityId = id;
    this.destination = destination;
    this.failureStage = failureStage;
  }
}

export const saveFilesForCreatedEntity = async ({
  createdEntity,
  saveFiles,
}: SaveFilesForCreatedEntityOptions): Promise<void> => {
  try {
    await saveFiles();
  } catch (cause) {
    if (cause instanceof CreatedEntityFileSaveError) {
      throw cause;
    }
    throw new CreatedEntityFileSaveError({ ...createdEntity, cause });
  }
};

/**
 * Saves files for a dual-mode create/edit form. Only a create-side failure is
 * classified as partial success because edits have no newly-created entity to
 * preserve or redirect to.
 */
export const saveFilesForOptionalCreatedEntity = async ({
  createdEntity,
  saveFiles,
}: SaveFilesForOptionalCreatedEntityOptions): Promise<void> => {
  if (!createdEntity) {
    await saveFiles();

    return;
  }

  await saveFilesForCreatedEntity({ createdEntity, saveFiles });
};

/**
 * Keeps the create and its secondary file persistence in one typed operation.
 * File hooks own their bounded retry policy; this helper only classifies the
 * terminal state after the file hook gives up or rejects without retrying.
 */
export const createWithFiles = async <TResult>({
  create,
  getCreatedEntity,
  saveFiles,
}: CreateWithFilesOptions<TResult>): Promise<
  CreateWithFilesResult<TResult>
> => {
  const result = await create();
  const createdEntity = getCreatedEntity(result);

  await saveFilesForCreatedEntity({
    createdEntity,
    saveFiles: () => saveFiles(createdEntity, result),
  });

  return { result, createdEntity };
};
